import React, { useState } from "react";
import {
  Modal,
  Table,
  Tag,
  Button,
  Space,
  Typography,
  Card,
  Statistic,
  Row,
  Col,
  Input,
} from "antd";
import {
  DownloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SearchOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import {
  IValidationResult,
  IValidationError,
} from "../../Services/PracticeDataRequestService";

const { Text, Title } = Typography;

interface CsvValidationModalProps {
  open: boolean;
  result: IValidationResult | null;
  onClose: () => void;
}

const downloadErrorReportCsv = (result: IValidationResult) => {
  const cleanTemplateName = (result.templateType || "Template").replace(
    /[^a-zA-Z0-9]/g,
    "_",
  );
  const filename = `${cleanTemplateName}_Validation_Errors.csv`;

  let csvContent = `Template Category,File Name,Total Rows,Valid Rows,Total Errors,Validation Status\n`;
  csvContent += `"${result.templateType}","${result.fileName}",${result.totalRows},${result.validRows},${result.errorCount},"${result.status}"\n\n`;
  csvContent += `Line Number,Column Name,Input Value,Error Description\n`;

  (result.errors || []).forEach((err: IValidationError) => {
    const escapedVal = (err.value || "").replace(/"/g, '""');
    const escapedMsg = (err.message || "").replace(/"/g, '""');
    csvContent += `${err.rowNumber},"${err.colName}","${escapedVal}","${escapedMsg}"\n`;
  });

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.setAttribute("href", url);
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const CsvValidationModal: React.FC<CsvValidationModalProps> = ({
  open,
  result,
  onClose,
}) => {
  const [searchFilter, setSearchFilter] = useState("");

  if (!result) return null;

  const isPassed = result.status === "Passed";
  const filteredErrors = (result.errors || []).filter((err) => {
    if (!searchFilter.trim()) return true;
    const q = searchFilter.toLowerCase();
    return (
      String(err.rowNumber).includes(q) ||
      err.colName.toLowerCase().includes(q) ||
      err.message.toLowerCase().includes(q) ||
      (err.value && err.value.toLowerCase().includes(q))
    );
  });

  const columns = [
    {
      title: "Line #",
      dataIndex: "rowNumber",
      key: "rowNumber",
      width: 80,
      render: (val: number) => <Tag color="blue">Row {val}</Tag>,
    },
    {
      title: "Column Name",
      dataIndex: "colName",
      key: "colName",
      width: 160,
      render: (text: string) => (
        <strong style={{ color: "#0078d4" }}>{text}</strong>
      ),
    },
    {
      title: "Input Value",
      dataIndex: "value",
      key: "value",
      width: 180,
      render: (text: string) =>
        text ? (
          <code
            style={{
              background: "#f5f5f5",
              padding: "2px 6px",
              borderRadius: 4,
            }}
          >
            {text}
          </code>
        ) : (
          <Text type="secondary">(empty)</Text>
        ),
    },
    {
      title: "Error Reason",
      dataIndex: "message",
      key: "message",
      render: (text: string) => <Text type="danger">{text}</Text>,
    },
  ];

  return (
    <Modal
      open={open}
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <WarningOutlined
            style={{ color: isPassed ? "#52c41a" : "#ff4d4f", fontSize: 20 }}
          />
          <span>File Validation Report - {result.templateType}</span>
        </div>
      }
      onCancel={onClose}
      width={850}
      footer={[
        <Button key="close" onClick={onClose}>
          Close
        </Button>,
        <Button
          key="download"
          type="primary"
          icon={<DownloadOutlined />}
          onClick={() => downloadErrorReportCsv(result)}
          style={{ backgroundColor: "#0078d4", borderColor: "#0078d4" }}
        >
          Download Error Report (.csv)
        </Button>,
      ]}
    >
      <div style={{ padding: "12px 0" }}>
        {/* Summary Cards */}
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <Card
              size="small"
              style={{ background: "#fafafa", textAlign: "center" }}
            >
              <Statistic title="Total Rows" value={result.totalRows} />
            </Card>
          </Col>
          <Col span={6}>
            <Card
              size="small"
              style={{ background: "#f6ffed", textAlign: "center" }}
            >
              <Statistic
                title="Valid Rows"
                value={result.validRows}
                valueStyle={{ color: "#52c41a" }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card
              size="small"
              style={{
                background: result.errorCount > 0 ? "#fff2f0" : "#fafafa",
                textAlign: "center",
              }}
            >
              <Statistic
                title="Errors Found"
                value={result.errorCount}
                valueStyle={{
                  color: result.errorCount > 0 ? "#ff4d4f" : "#52c41a",
                }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card
              size="small"
              style={{ background: "#fafafa", textAlign: "center" }}
            >
              <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 4 }}>
                Status
              </div>
              {isPassed ? (
                <Tag
                  color="success"
                  icon={<CheckCircleOutlined />}
                  style={{ fontSize: 13, padding: "4px 10px" }}
                >
                  PASSED
                </Tag>
              ) : (
                <Tag
                  color="error"
                  icon={<CloseCircleOutlined />}
                  style={{ fontSize: 13, padding: "4px 10px" }}
                >
                  FAILED
                </Tag>
              )}
            </Card>
          </Col>
        </Row>

        {/* File info bar */}
        <div
          style={{
            background: "#f8fafc",
            padding: "8px 12px",
            borderRadius: 6,
            marginBottom: 16,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Text type="secondary">
            File Name: <strong>{result.fileName}</strong>
          </Text>
          <Input
            placeholder="Search error details..."
            prefix={<SearchOutlined />}
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
            style={{ width: 220 }}
            size="small"
            allowClear
          />
        </div>

        {/* Error Table */}
        <Table
          dataSource={filteredErrors}
          columns={columns}
          rowKey={(record, idx) =>
            `${record.rowNumber}_${record.colName}_${idx}`
          }
          size="small"
          pagination={{ pageSize: 8, showSizeChanger: true }}
          locale={{
            emptyText: isPassed
              ? "No validation errors found! All rows passed cleanly."
              : "No errors match your search.",
          }}
        />
      </div>
    </Modal>
  );
};

export default CsvValidationModal;
